
import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Import, FileText, AlertCircle } from "lucide-react";
import { db } from "@/integrations/cloudflare/client";
import { CraftIdea } from "@/components/tools/craft-generator/types";

type ImportFormat = "json" | "csv";

interface CraftIdeasImporterProps {
  onImportComplete: () => void;
}

const CraftIdeasImporter: React.FC<CraftIdeasImporterProps> = ({ onImportComplete }) => {
  const { toast } = useToast();
  const [importFormat, setImportFormat] = useState<ImportFormat>("json");
  const [importText, setImportText] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importStats, setImportStats] = useState<{
    total: number;
    success: number;
    failed: number;
  } | null>(null);

  const handleImport = async () => {
    if (!importText.trim()) {
      toast({
        title: "No data to import",
        description: "Please enter some data to import",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    setImportStats(null);

    try {
      let craftIdeasToProcess: Record<string, any>[] = [];

      if (importFormat === "json") {
        try {
          const parsed = JSON.parse(importText);
          craftIdeasToProcess = Array.isArray(parsed) ? parsed : [parsed];
        } catch (error) {
          toast({
            title: "Invalid JSON",
            description: "Please check your JSON format and try again",
            variant: "destructive",
          });
          setIsImporting(false);
          return;
        }
      } else if (importFormat === "csv") {
        // Simple CSV parser (assumes first row is header)
        const rows = importText.trim().split("\n");
        if (rows.length < 2) {
          toast({
            title: "Invalid CSV",
            description: "CSV must have a header row and at least one data row",
            variant: "destructive",
          });
          setIsImporting(false);
          return;
        }

        const headers = rows[0].split(",").map(h => h.trim().toLowerCase());
        
        // Check required columns
        const requiredColumns = ["title", "instructions", "required_supplies", "difficulty"];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast({
            title: "Missing required columns",
            description: `CSV is missing these columns: ${missingColumns.join(", ")}`,
            variant: "destructive",
          });
          setIsImporting(false);
          return;
        }

        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(",").map(v => v.trim());
          if (values.length !== headers.length) continue;
          
          const idea: Record<string, any> = {};
          headers.forEach((header, index) => {
            if (header === "required_supplies") {
              // Convert comma-separated list to array
              idea[header] = values[index].split(";").map(s => s.trim()).filter(Boolean);
            } else {
              idea[header] = values[index];
            }
          });
          
          craftIdeasToProcess.push(idea);
        }
      }

      // Validate each craft idea
      const validIdeas: {
        title: string;
        instructions: string;
        required_supplies: string[];
        difficulty: string;
        time_required?: string | null;
      }[] = [];
      const invalidIdeas: Record<string, any>[] = [];

      for (const idea of craftIdeasToProcess) {
        if (!idea.title || !idea.instructions || !idea.required_supplies || !idea.difficulty) {
          invalidIdeas.push(idea);
          continue;
        }
        
        // Ensure required_supplies is an array
        if (!Array.isArray(idea.required_supplies)) {
          idea.required_supplies = [idea.required_supplies as unknown as string];
        }
        
        // Create a valid idea object with all required fields
        const validIdea = {
          title: idea.title,
          instructions: idea.instructions,
          required_supplies: idea.required_supplies as string[],
          difficulty: idea.difficulty,
          time_required: idea.time_required || null
        };
        
        validIdeas.push(validIdea);
      }

      // Insert valid ideas into the database
      if (validIdeas.length > 0) {
        const { error } = await db
          .from("craft_ideas")
          .insert(validIdeas);

        if (error) throw error;
      }

      setImportStats({
        total: craftIdeasToProcess.length,
        success: validIdeas.length,
        failed: invalidIdeas.length
      });

      toast({
        title: "Import complete",
        description: `Successfully imported ${validIdeas.length} craft ideas. ${invalidIdeas.length} failed validation.`,
        variant: invalidIdeas.length > 0 ? "default" : "default",
      });

      // Reset form after successful import
      if (validIdeas.length > 0) {
        setImportText("");
        onImportComplete();
      }
    } catch (error) {
      console.error("Error importing craft ideas:", error);
      toast({
        title: "Import failed",
        description: "An error occurred while importing craft ideas. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleFormatChange = (value: string) => {
    setImportFormat(value as ImportFormat);
  };

  const getPlaceholderText = () => {
    if (importFormat === "json") {
      return `[
  {
    "title": "Paper Snowflake",
    "instructions": "1. Fold paper in half diagonally. 2. Cut designs along the folded edge. 3. Unfold to reveal your snowflake.",
    "required_supplies": ["Paper", "Scissors"],
    "difficulty": "Easy",
    "time_required": "15 minutes"
  }
]`;
    } else {
      return `title,instructions,required_supplies,difficulty,time_required
Paper Snowflake,1. Fold paper diagonally. 2. Cut designs. 3. Unfold.,Paper;Scissors,Easy,15 minutes
Pine Cone Ornament,1. Collect pine cones. 2. Paint them. 3. Add string.,Pine Cones;Paint;String,Medium,30 minutes`;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Import className="h-5 w-5 text-elfGreen" />
          Bulk Import Craft Ideas
        </CardTitle>
        <CardDescription>
          Import multiple craft ideas at once using JSON or CSV format
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="import-format">Format</Label>
          <Select 
            value={importFormat} 
            onValueChange={handleFormatChange}
          >
            <SelectTrigger id="import-format" className="w-full md:w-40">
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="json">JSON</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="import-data">Paste your data below</Label>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 gap-1 text-xs"
              onClick={() => setImportText(getPlaceholderText())}
            >
              <FileText className="h-3 w-3" />
              Load Example
            </Button>
          </div>
          <Textarea
            id="import-data"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={getPlaceholderText()}
            className="font-mono text-sm min-h-[300px]"
          />
        </div>

        {importFormat === "csv" && (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-700">CSV Format Note</p>
              <p className="mt-1 text-amber-600">
                For the "required_supplies" column, separate multiple supplies with semicolons (;)
                instead of commas to avoid breaking the CSV format.
              </p>
            </div>
          </div>
        )}

        {importStats && (
          <div className="bg-elfGreen-lighter border border-elfGreen rounded-md p-3">
            <h4 className="font-medium">Import Results</h4>
            <ul className="mt-2 space-y-1">
              <li>Total items: {importStats.total}</li>
              <li>Successfully imported: {importStats.success}</li>
              <li>Failed validation: {importStats.failed}</li>
            </ul>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          onClick={handleImport}
          disabled={isImporting || !importText.trim()}
          className="bg-elfGreen hover:bg-elfGreen-dark gap-2"
        >
          <Import className="h-4 w-4" />
          {isImporting ? "Importing..." : "Import Craft Ideas"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CraftIdeasImporter;
